import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useToast } from '../ui/Toast.js'
import {
  api,
  odswiezToken,
  ustawIdUzytkownika,
  ustawToken,
  wylogujZadanie,
  zalogujZadanie,
} from './api.js'
import { wyczysc } from './db.js'
import { ustawRaportOdrzucen } from './kolejka.js'
import { polaczRealtime, synchronizujWszystko } from './sync.js'
import type { Uzytkownik } from './typy.js'

type Stan = 'sprawdzanie' | 'zalogowany' | 'niezalogowany'

interface Sesja {
  stan: Stan
  uzytkownik: Uzytkownik | null
  zaloguj(email: string, haslo: string): Promise<void>
  wyloguj(): Promise<void>
}

const Kontekst = createContext<Sesja | null>(null)

/**
 * Odtwarza sesję przy starcie z ciasteczka odświeżającego, a po zalogowaniu
 * uruchamia pełną synchronizację i nasłuch zmian. Dzięki temu po wejściu na
 * stronę dane są na miejscu, zanim użytkownik zdąży kliknąć.
 */
export function DostawcaSesji({ children }: { children: ReactNode }) {
  const [stan, ustawStan] = useState<Stan>('sprawdzanie')
  const [uzytkownik, ustawUzytkownika] = useState<Uzytkownik | null>(null)
  const toast = useToast()

  useEffect(() => {
    let aktualne = true
    void (async () => {
      const udalo = await odswiezToken()
      if (!aktualne) return
      if (!udalo) {
        ustawStan('niezalogowany')
        return
      }
      try {
        const dane = await api.get<Uzytkownik>('/api/auth/me')
        if (!aktualne) return
        ustawIdUzytkownika(dane.id)
        ustawUzytkownika(dane)
        ustawStan('zalogowany')
      } catch {
        if (aktualne) ustawStan('niezalogowany')
      }
    })()
    return () => {
      aktualne = false
    }
  }, [])

  // Synchronizacja i rozgłośnia żyją tylko wtedy, gdy ktoś jest zalogowany.
  useEffect(() => {
    if (stan !== 'zalogowany') return
    void synchronizujWszystko().catch(() => undefined)
    // Operacja odrzucona przez serwer została już cofnięta lokalnie —
    // użytkownik musi wiedzieć, że jego zmiana nie przeszła.
    ustawRaportOdrzucen((wynik) => {
      for (const odrzucona of wynik.odrzucone) {
        toast.pokazBlad(`Zmiana nie przeszła: ${odrzucona.powod}`)
      }
    })

    const rozlacz = polaczRealtime()
    return () => {
      ustawRaportOdrzucen(null)
      rozlacz()
    }
  }, [stan, toast])

  const zaloguj = useCallback(async (email: string, haslo: string) => {
    const dane = await zalogujZadanie(email, haslo)
    ustawIdUzytkownika(dane.user.id)
    ustawUzytkownika(dane.user)
    ustawStan('zalogowany')
  }, [])

  const wyloguj = useCallback(async () => {
    await wylogujZadanie().catch(() => undefined)
    ustawToken(null)
    ustawIdUzytkownika(null)
    await wyczysc()
    ustawUzytkownika(null)
    ustawStan('niezalogowany')
  }, [])

  const wartosc = useMemo<Sesja>(
    () => ({ stan, uzytkownik, zaloguj, wyloguj }),
    [stan, uzytkownik, zaloguj, wyloguj],
  )

  return <Kontekst.Provider value={wartosc}>{children}</Kontekst.Provider>
}

export function useSesja(): Sesja {
  const sesja = useContext(Kontekst)
  if (sesja === null) throw new Error('useSesja wymaga DostawcySesji')
  return sesja
}
